<template>
  <div class="payment-fallback insicht insicht--fade-up">
    <form
      class="payment-fallback__wrapper"
      novalidate
      @submit.prevent="onSubmit"
    >
      <h2
        v-if="title"
        class="payment-fallback__title"
        v-sanitize="title"
      />
      <h3
        v-if="subtitle"
        class="payment-fallback__subtitle"
        v-sanitize="subtitle"
      />
      <GeneralMessage
				ref="message"
				v-if="response.message"
				:message="response.message"
				:type="response.type"
				stick
			/>

      <ValidationProvider
				name="location"
				rules="required"
        class="payment-fallback__location"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
			>
				<FormInputSelect
					v-model="channel"
					:options="locationOptions"
					:label="$t('paymentfallback.locationLabel')"
					:disabled="formLocked"
					required
          @input="onChannelSelect"
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>

				<OrderBoxLocation
					v-if="location"
					:location="formatLocation(location)"
					shipping-method="takeaway"
					no-click
				/>
			</ValidationProvider>

      <div class="payment-fallback__amount">
        <span
          class="payment-fallback__amount-prefix"
          v-html="'CHF'"
        />
        <input
          v-model.number="amount"
          type="number"
          min="0"
          step="0.05"
          class="payment-fallback__amount-input"
          :disabled="formLocked"
          required
          @blur="onBlur"
        >
      </div>
      <Button
        type="submit"
        level="primary"
        :text="$t('paymentfallback.orderButton')"
        :disabled="submitLocked"
      />
    </form>
  </div>
</template>

<script>
import Vue from 'vue'
import { ValidationProvider, ValidationObserver } from "vee-validate"
import { mapGetters } from "vuex"
import scrrroll from 'scrrroll'
import FormInput from "@/components/FormInput"
import FormInputSelect from "@/components/FormInputSelectCustom"
import GenericMixin from '@/mixins/GenericVueMixin'
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import OrderBoxLocation from '@/components/OrderBoxLocation'
import { getUrlParams, makeUrl } from '@/utils/urlParams';

export default {
	mixins: [
		GenericMixin,
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputSelect,
		Button,
		GeneralMessage,
    OrderBoxLocation,
	},
  props: {},
  data() {
    return {
      title: this.$root.options.title,
      subtitle: this.$root.options.subtitle,
      channel: null,
      amount: null,
      response: {
				type: 'info',
				message: '',
			},
    }
  },
  computed: {
    ...mapGetters('location', [
			'storeLocations',
		]),
    ...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
    formLocked() {
      return this.loading
    },
    submitLocked() {
      return this.formLocked || !this.amount || this.amount <= 0 || !this.channel
    },
    location() {
      return this.storeLocations.find(l => l.code === this.channel)
    },
    locationOptions() {
			const locationsByBrand = this.storeLocations.reduce((groups, location) => {
        if (location.isLocationOnline && location.openingHours.length) {
          if (!groups[location.brand]) {
            groups[location.brand] = []
          }

          groups[location.brand].push(location);
        }

        return groups;
      }, {});

      return Object.entries(locationsByBrand)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([brand, locations]) => {
          return {
            group: true,
            label: this.$t(`brands.${brand}`),
            options: locations.map(location => {
              return {
                label: location.name,
                value: location.code,
              }
            })
          }
        });
		},
  },
  methods: {
    formatLocation(location) {
			let formatted = {
				image: location.image,
				name: location.name,
				code: location.code,
				brand: location.brand,
				region: location.region,
				address: location.address ?? {
					street: location.street,
					zip: location.zip,
					city: location.city,
				},
				distance: location.distance != null ? this.formatDistance(location.distance) : null,
				tags: location.tags ?? [],
			}

			return formatted
		},
    onBlur() {
      this.amount = this.$options.filters.fwgpricecent(this.amount * 100, 'short')
    },
    onChannelSelect(channel) {
      history.replaceState({}, '', makeUrl({ channel }))
    },
    async onSubmit(e) {
			e.preventDefault();
      this.onBlur()
			this.loading = true;

			if (this.amount) {
				try {
          this.response.message = ''
          this.response.type = 'info'

					await this.$store.dispatch('cart/createPaymentFallbackOrder', {
						amount: this.amount * 100,
            channel: this.channel,
					});
				}
				catch(err) {
          this.isDebug() && console.error(err);
					if (err.response && err.response.data.message) {
						this.response.message = err.response.data.errors && Object.values(err.response.data.errors).length
							? Array.from(new Set(Object.values(err.response.data.errors).flat())).join()
							: err.response.data.message
					} else {
						this.response.message = this.$t('forms.errors.generic');
					}
					this.response.type = 'error';
					this.$nextTick(() => {
						this.$refs.message.scrollTo();
					});
					this.loading = false;
				}
			} else {
				await this.$nextTick();
				const inputError = this.$refs.form.$el.querySelector('.input-error');

				if (inputError) {
					scrrroll.to(inputError.parentNode)
				}
				this.loading = false
			}
		},
  },
  async created() {
    const params = getUrlParams()
    this.loading = true
    await this.$store.dispatch('location/getStoreLocations', { brand: null })

    if (params.channel && this.storeLocations.find(l => l.code === params.channel)) {
      this.channel = params.channel
    }
    this.loading = false

  },
}
</script>
